import React, { useState, useEffect, useCallback, useRef, useId } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { campaignsAPI, templatesAPI, buyersAPI, sendingIntelligenceAPI } from '../services/api';
import CampaignTemplateEditor from '../components/CampaignTemplateEditor';
import SendingPlanPanel from '../components/SendingPlanPanel';
import { AlertCircle, ArrowLeft, Ban, CheckCircle2, ChevronDown, ChevronUp, Loader2, RotateCw, Send, Trash2 } from 'lucide-react';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import useConfirm from '../hooks/useConfirm';
import SetupErrorMessage from '../components/SetupErrorMessage';
import { useAuth } from '../contexts/AuthContext';
import { campaignStatusClass } from './campaignStatus';
import './CampaignsPage.css';
import './CampaignDetailPage.css';

const formatDateTime = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

function RecipientsPanel({ campaignId, pending, onChanged, onError, reasons }) {
  const [expanded, setExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const handleSearch = async (term) => {
    setSearchTerm(term);
    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await buyersAPI.getAll({ search: term, limit: 10 });
      setSearchResults(res.data.buyers || []);
    } catch (err) {
      // Search box failure isn't critical enough to surface as a page-level error
    }
  };

  const handleAdd = async (buyerId) => {
    setBusy(true);
    try {
      await campaignsAPI.updateRecipients(campaignId, { add: [buyerId] });
      setSearchResults([]);
      setSearchTerm('');
      await onChanged();
    } catch (err) {
      onError(err.response?.data?.error || 'Could not add recipient');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (buyerId) => {
    setBusy(true);
    try {
      await campaignsAPI.updateRecipients(campaignId, { remove: [buyerId] });
      await onChanged();
    } catch (err) {
      onError(err.response?.data?.error || 'Could not remove recipient');
    } finally {
      setBusy(false);
    }
  };

  // A buyer can have more than one pending step; show one row per buyer,
  // with every step they're still pending for.
  const pendingBuyers = [];
  const buyerIndexByBuyerId = new Map();
  pending.forEach((row) => {
    if (!buyerIndexByBuyerId.has(row.buyer_id)) {
      buyerIndexByBuyerId.set(row.buyer_id, pendingBuyers.length);
      pendingBuyers.push({ ...row, message_indexes: [] });
    }
    pendingBuyers[buyerIndexByBuyerId.get(row.buyer_id)].message_indexes.push(row.message_index);
  });

  return (
    <section className="cd-card">
      <button
        type="button"
        className="cd-collapse"
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        <span className="cd-card-title">
          Recipients <span className="cd-card-count">{pendingBuyers.length} pending</span>
        </span>
        {expanded ? <ChevronUp className="cd-collapse-icon" /> : <ChevronDown className="cd-collapse-icon" />}
      </button>
      {expanded && (
        <div className="cd-collapse-body">
          <input
            type="text"
            className="cd-input cd-input--full"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search buyers to add..."
          />
          {searchResults.length > 0 && (
            <ul className="cd-list cd-list--boxed">
              {searchResults.map((b) => (
                <li key={b.id} className="cd-list-row">
                  <span>{b.business_name}</span>
                  <button
                    type="button"
                    className="cd-link-btn"
                    disabled={busy}
                    onClick={() => handleAdd(b.id)}
                  >
                    Add
                  </button>
                </li>
              ))}
            </ul>
          )}
          <h3 className="cd-subheading">Pending</h3>
          {pendingBuyers.length === 0 ? (
            <p className="cd-empty">No pending recipients.</p>
          ) : (
            <ul className="cd-list">
              {pendingBuyers.map((row) => {
                const steps = row.message_indexes
                  .filter((i) => i !== null && i !== undefined)
                  .sort((a, b) => a - b)
                  .map((i) => i + 1);
                const stepLabel = steps.length === 0 ? '' : steps.length === 1 ? `Message ${steps[0]}` : `Messages ${steps.join(', ')}`;
                return (
                <li key={row.buyer_id} className="cd-list-row">
                  <span className="cd-list-main">
                    {row.business_name}
                    {stepLabel && <span className="cd-meta-inline">({stepLabel})</span>}
                    {row.email_verification_status && (
                      <span className="cd-meta-inline">
                        [{row.email_verification_status}
                        {typeof row.email_verification_score === 'number' ? ` ${row.email_verification_score}` : ''}]
                      </span>
                    )}
                    {reasons && reasons[row.buyer_id] && (
                      <span className="cd-meta-block">{reasons[row.buyer_id]}</span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="cd-link-btn cd-link-btn--danger"
                    disabled={busy}
                    onClick={() => handleRemove(row.buyer_id)}
                  >
                    Remove
                  </button>
                </li>
                );
              })}
            </ul>
          )}
          <p className="cd-note">
            Removing only cancels their pending message in this campaign — it does not mark them do-not-contact.
          </p>
        </div>
      )}
    </section>
  );
}

function MessageEditorPanel({ campaignId, campaign, template, pending, onChanged, onError, onSuccess }) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [scheduleDrafts, setScheduleDrafts] = useState({});
  const [busy, setBusy] = useState(false);

  const stepIndexes = Array.from(
    new Set(pending.map((r) => r.message_index).filter((i) => i !== null && i !== undefined))
  ).sort((a, b) => a - b);

  const handleSaveContent = async (templateOverrides) => {
    setBusy(true);
    try {
      if (templateOverrides.messages) {
        for (let i = 0; i < templateOverrides.messages.length; i++) {
          const m = templateOverrides.messages[i];
          await campaignsAPI.updatePendingContent(campaignId, { messageIndex: i, subject: m.subject, body: m.body });
        }
      } else {
        await campaignsAPI.updatePendingContent(campaignId, {
          messageIndex: 0,
          subject: templateOverrides.subject,
          body: templateOverrides.body
        });
      }
      onSuccess('Message content updated for unsent messages');
      await onChanged();
    } catch (err) {
      onError(err.response?.data?.error || 'Could not update message content');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveSchedule = async (index) => {
    const delayHours = scheduleDrafts[index];
    if (delayHours === undefined || delayHours === '') return;
    setBusy(true);
    try {
      await campaignsAPI.updateSchedule(campaignId, { messageIndex: index, delayHours: Number(delayHours) });
      onSuccess(`Schedule updated for message ${index + 1}`);
      await onChanged();
    } catch (err) {
      onError(err.response?.data?.error || 'Could not update schedule');
    } finally {
      setBusy(false);
    }
  };

  if (!template) return null;

  return (
    <section className="cd-card">
      <div className="cd-card-head">
        <div>
          <h2 className="cd-card-title">Messages</h2>
          <p className="cd-card-sub">Edit remaining messages. Changes here only affect messages not yet sent.</p>
        </div>
        <button className="btn btn-secondary cd-btn-sm" onClick={() => setEditorOpen(true)} disabled={busy}>
          Edit content
        </button>
      </div>

      {stepIndexes.length > 0 && (
        <div>
          <h3 className="cd-subheading">Adjust delay</h3>
          {stepIndexes.map((index) => (
            <div key={index} className="cd-delay-row">
              <span className="cd-delay-label">Message {index + 1}</span>
              <input
                type="number"
                className="cd-input cd-input--delay"
                placeholder="New delay (hours)"
                value={scheduleDrafts[index] ?? ''}
                onChange={(e) => setScheduleDrafts((d) => ({ ...d, [index]: e.target.value }))}
              />
              <button disabled={busy} onClick={() => handleSaveSchedule(index)} className="btn btn-secondary cd-btn-sm">
                Update delay
              </button>
            </div>
          ))}
        </div>
      )}

      <CampaignTemplateEditor
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        campaign={campaign}
        template={template}
        onSave={handleSaveContent}
      />
    </section>
  );
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// "Send a test copy" dialog: sends the failed message to the address typed
// here (the account email by default), never to the prospect.
function TestCopyDialog({ campaignId, group, defaultEmail, onClose }) {
  const isSms = group.type === 'sms';
  const buyerName = group.business_name || `Buyer #${group.buyer_id}`;
  const fieldId = useId();
  const errorId = useId();
  const [to, setTo] = useState(isSms ? '' : (defaultEmail || ''));
  const [fieldError, setFieldError] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = to.trim();
    if (!value) {
      setFieldError(isSms ? 'Enter a phone number.' : 'Enter an email address.');
      return;
    }
    if (!isSms && !EMAIL_PATTERN.test(value)) {
      setFieldError('Enter a valid email address.');
      return;
    }
    setFieldError('');
    setSending(true);
    setResult(null);
    const targetRow = group.rows[0]; // earliest step is usually the real root cause
    try {
      const res = await campaignsAPI.testSendMessage(campaignId, targetRow.id, value);
      setResult(res.data.success
        ? { ok: true, text: `Test copy sent to ${value}` }
        : { ok: false, text: res.data.error || 'The test copy could not be sent.' });
    } catch (err) {
      setResult({ ok: false, text: err.response?.data?.error || 'The test copy could not be sent.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Send a test copy" size="small">
      <form className="cd-test-form" onSubmit={handleSubmit} noValidate>
        <p className="cd-test-intro">
          We will send a copy of this message to the address below, not to {buyerName}.
        </p>
        <label className="cd-test-label" htmlFor={fieldId}>
          {isSms ? 'Phone number' : 'Email address'}
        </label>
        <input
          id={fieldId}
          type={isSms ? 'tel' : 'email'}
          required
          className={`cd-input cd-input--full${fieldError ? ' cd-input--invalid' : ''}`}
          value={to}
          onChange={(e) => { setTo(e.target.value); if (fieldError) setFieldError(''); }}
          placeholder={isSms ? '+1 555 000 0000' : 'you@example.com'}
          aria-invalid={fieldError ? 'true' : 'false'}
          aria-describedby={fieldError ? errorId : undefined}
          disabled={sending || !!result}
        />
        {fieldError && <div id={errorId} className="cd-test-field-error">{fieldError}</div>}
        {sending && (
          <div className="cd-test-status" role="status">
            <Loader2 className="cd-act-icon cd-spin" aria-hidden="true" />
            Sending test copy...
          </div>
        )}
        {result && (
          <div
            className={`cd-test-result ${result.ok ? 'cd-test-result--ok' : 'cd-test-result--error'}`}
            role={result.ok ? 'status' : 'alert'}
          >
            {result.ok
              ? <CheckCircle2 className="cd-act-icon" aria-hidden="true" />
              : <AlertCircle className="cd-act-icon" aria-hidden="true" />}
            <span>{result.text}</span>
          </div>
        )}
        <div className="cd-test-actions">
          {result ? (
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          ) : (
            <>
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={sending}>
                {sending ? 'Sending...' : 'Send test copy'}
              </button>
            </>
          )}
        </div>
      </form>
    </Modal>
  );
}

function FailuresPanel({ campaignId, failures, onChanged, onError }) {
  const { user } = useAuth();
  const [busyBuyerId, setBusyBuyerId] = useState(null);
  const [rowStatus, setRowStatus] = useState({});
  const [confirmAction, setConfirmAction] = useState(null);
  const [testGroup, setTestGroup] = useState(null);

  // One action set per buyer, not per failed message row -- a buyer with
  // two failed steps still only needs one "retry / remove / test" decision.
  const handleRetry = async (group) => {
    setBusyBuyerId(group.buyer_id);
    setRowStatus((s) => ({ ...s, [group.buyer_id]: null }));
    try {
      for (const row of group.rows) {
        await campaignsAPI.retryMessage(campaignId, row.id);
      }
      setRowStatus((s) => ({ ...s, [group.buyer_id]: 'Retry queued' }));
      await onChanged();
    } catch (err) {
      onError(err.response?.data?.error || 'Could not retry message');
    } finally {
      setBusyBuyerId(null);
    }
  };

  // alsoSuppress = "Do not contact": also marks the prospect Do Not Contact
  // for all future campaigns and cancels their other pending steps here.
  const handleRemove = async (group, alsoSuppress) => {
    setBusyBuyerId(group.buyer_id);
    setRowStatus((s) => ({ ...s, [group.buyer_id]: null }));
    try {
      for (const row of group.rows) {
        await campaignsAPI.removeMessage(campaignId, row.id, alsoSuppress);
      }
      await onChanged();
    } catch (err) {
      onError(err.response?.data?.error || 'Could not remove message');
    } finally {
      setBusyBuyerId(null);
    }
  };

  if (failures.length === 0) return null;

  // Group by buyer: the same person can fail more than once (e.g. a real
  // failure on message 1, then a cascading "marked as Do Not Contact" on
  // message 2) -- show them as one buyer with each failed step listed,
  // not as unrelated-looking duplicate rows.
  const groupOrder = [];
  const groupsByBuyer = {};
  failures.forEach((row) => {
    if (!groupsByBuyer[row.buyer_id]) {
      groupsByBuyer[row.buyer_id] = { buyer_id: row.buyer_id, business_name: row.business_name, type: row.type, rows: [] };
      groupOrder.push(row.buyer_id);
    }
    groupsByBuyer[row.buyer_id].rows.push(row);
  });
  const groups = groupOrder.map((buyerId) => groupsByBuyer[buyerId]);

  const confirmName = confirmAction
    ? (confirmAction.group.business_name || `Buyer #${confirmAction.group.buyer_id}`)
    : '';
  const confirmIsDnc = confirmAction?.kind === 'dnc';

  return (
    <section className="cd-card cd-failures">
      <div className="cd-card-head cd-failures-head">
        <div>
          <h2 className="cd-card-title">
            Delivery failures{' '}
            <span className="cd-card-count cd-card-count--danger">
              {groups.length} {groups.length === 1 ? 'buyer' : 'buyers'}
              {failures.length !== groups.length ? `, ${failures.length} messages` : ''}
            </span>
          </h2>
          <p className="cd-card-sub">
            Retry sends the failed messages again. Remove cancels them for that buyer. Do not contact also skips them in every future campaign. Send test copy sends the message to you, not the buyer.
          </p>
        </div>
      </div>
      <div className="failures-table cd-failures-table">
        <div className="failures-table-header">
          <span>Buyer</span>
          <span>Channel</span>
          <span>Reason(s)</span>
          <span className="cd-failures-actions-head">Actions</span>
        </div>
        {groups.map((group) => {
          const buyerName = group.business_name || `Buyer #${group.buyer_id}`;
          const rowBusy = busyBuyerId === group.buyer_id;
          const status = rowStatus[group.buyer_id];
          return (
          <div key={group.buyer_id} className="failures-row">
            <div className="cd-failure-buyer">
              <strong>{buyerName}</strong>
            </div>
            <div className="cd-failure-channel">
              <span className="failures-chip">{(group.type || '').toUpperCase()}</span>
            </div>
            <div className="cd-failure-reasons">
              {group.rows.map((row) => (
                <div key={row.id} className="cd-failure-reason">
                  <div className="cd-failure-step">
                    {row.message_index === null || row.message_index === undefined
                      ? 'Step unknown (older data)'
                      : `Message ${row.message_index + 1}`}
                  </div>
                  <div className="cd-failure-error" title={row.error_message}><SetupErrorMessage message={row.error_message} /></div>
                </div>
              ))}
            </div>
            <div className="cd-failure-actions-cell">
              <div className="cd-failure-actions" role="group" aria-label={`Actions for ${buyerName}`} aria-busy={rowBusy}>
                <button
                  type="button"
                  className="cd-act-btn"
                  disabled={rowBusy}
                  onClick={() => handleRetry(group)}
                  title="Send the failed messages to this buyer again"
                  aria-label={`Retry failed messages for ${buyerName}`}
                >
                  <RotateCw className="cd-act-icon" aria-hidden="true" />
                  <span className="cd-act-label">Retry</span>
                </button>
                <button
                  type="button"
                  className="cd-act-btn cd-act-btn--danger"
                  disabled={rowBusy}
                  onClick={() => setConfirmAction({ kind: 'remove', group })}
                  title="Cancel the failed messages; the buyer stays in your prospects"
                  aria-label={`Remove ${buyerName} from this campaign`}
                >
                  <Trash2 className="cd-act-icon" aria-hidden="true" />
                  <span className="cd-act-label">Remove</span>
                </button>
                <button
                  type="button"
                  className="cd-act-btn cd-act-btn--danger"
                  disabled={rowBusy}
                  onClick={() => setConfirmAction({ kind: 'dnc', group })}
                  title="Remove and skip this buyer in all future campaigns"
                  aria-label={`Remove ${buyerName} and add them to Do Not Contact`}
                >
                  <Ban className="cd-act-icon" aria-hidden="true" />
                  <span className="cd-act-label">Do not contact</span>
                </button>
                <button
                  type="button"
                  className="cd-act-btn"
                  disabled={rowBusy}
                  onClick={() => setTestGroup(group)}
                  title="Send a copy of the failed message to you, not the buyer"
                  aria-label={`Send a test copy of the failed message for ${buyerName}`}
                >
                  <Send className="cd-act-icon" aria-hidden="true" />
                  <span className="cd-act-label">Send test copy</span>
                </button>
              </div>
              {(rowBusy || status) && (
                <div className="cd-failure-status" role="status">
                  {rowBusy ? 'Working...' : status}
                </div>
              )}
            </div>
          </div>
          );
        })}
      </div>

      <ConfirmDialog
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => handleRemove(confirmAction.group, confirmIsDnc)}
        title={confirmIsDnc ? 'Add to Do Not Contact?' : 'Remove from this campaign?'}
        message={confirmIsDnc
          ? `Remove ${confirmName} and add them to Do Not Contact? They will be skipped by all future campaigns. Their other pending messages in this campaign are cancelled.`
          : `Remove ${confirmName} from this campaign? Their failed message will be cancelled. They stay in your prospects list.`}
        confirmText={confirmIsDnc ? 'Remove and do not contact' : 'Remove'}
        variant={confirmIsDnc ? 'danger' : 'primary'}
      />

      {testGroup && (
        <TestCopyDialog
          campaignId={campaignId}
          group={testGroup}
          defaultEmail={user?.email}
          onClose={() => setTestGroup(null)}
        />
      )}
    </section>
  );
}

function CampaignDetailPage() {
  const [confirm, confirmDialog] = useConfirm();
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [template, setTemplate] = useState(null);
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [excludedBuyers, setExcludedBuyers] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [includingBuyerId, setIncludingBuyerId] = useState(null);
  const pollRef = useRef(null);

  const fetchLive = useCallback(async () => {
    try {
      const res = await campaignsAPI.getLive(id);
      setLive(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load campaign status');
    }
  }, [id]);

  const loadExcluded = useCallback(async () => {
    try {
      const res = await sendingIntelligenceAPI.getExcluded(id);
      setExcludedBuyers(res.data.buyers || []);
    } catch (err) {
      // Non-critical -- Excluded section just stays empty on failure
    }
  }, [id]);

  useEffect(() => {
    loadExcluded();
  }, [loadExcluded]);

  const usesSendingIntelligence = !!(campaign && (campaign.ramp_enabled || campaign.prioritize_deliverable_enabled));
  const liveStatus = live?.status || campaign?.status;

  const loadBreakdown = useCallback(async () => {
    if (!usesSendingIntelligence) return;
    try {
      const res = await sendingIntelligenceAPI.getBreakdown(id);
      setBreakdown(res.data);
    } catch (err) {
      // Non-critical -- the Sending Plan panel simply stays hidden on failure
    }
  }, [id, usesSendingIntelligence]);

  useEffect(() => {
    setBreakdown(null);
  }, [id]);

  useEffect(() => {
    loadBreakdown();
    if (!usesSendingIntelligence || !['running', 'scheduled'].includes(liveStatus)) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') loadBreakdown();
    }, 60000);
    return () => clearInterval(timer);
  }, [loadBreakdown, usesSendingIntelligence, liveStatus]);

  const refreshAfterRecipientChange = useCallback(async () => {
    await fetchLive();
    await loadBreakdown();
  }, [fetchLive, loadBreakdown]);

  const reasonsByBuyer = {};
  if (usesSendingIntelligence && breakdown) {
    breakdown.days.forEach((d) => d.buyers.forEach((b) => { reasonsByBuyer[b.buyer_id] = b.reason; }));
    breakdown.follow_ups.buyers.forEach((b) => {
      if (!reasonsByBuyer[b.buyer_id]) reasonsByBuyer[b.buyer_id] = b.reason;
    });
  }

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const campaignRes = await campaignsAPI.getById(id);
        if (!mounted) return;
        const c = campaignRes.data.campaign || campaignRes.data;
        setCampaign(c);
        if (c.template_id) {
          const templateRes = await templatesAPI.getById(c.template_id);
          if (mounted) setTemplate(templateRes.data.template || templateRes.data);
        }
      } catch (err) {
        if (mounted) setError(err.response?.data?.error || 'Failed to load campaign');
      }
      await fetchLive();
      if (mounted) setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [id, fetchLive]);

  useEffect(() => {
    pollRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') fetchLive();
    }, 10000);
    return () => clearInterval(pollRef.current);
  }, [fetchLive]);

  const flashSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  const handlePause = async () => {
    setActionLoading(true);
    try {
      await campaignsAPI.pause(id);
      await fetchLive();
      flashSuccess('Campaign paused');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not pause campaign');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    setActionLoading(true);
    try {
      await campaignsAPI.resume(id);
      await fetchLive();
      flashSuccess('Campaign resumed');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not resume campaign');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStop = async () => {
    if (!(await confirm({
      title: 'Stop campaign',
      message: 'Stop this campaign? Pending messages will be cancelled.',
      confirmText: 'Stop campaign'
    }))) return;
    setActionLoading(true);
    try {
      await campaignsAPI.stop(id);
      await fetchLive();
      flashSuccess('Campaign stopped');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not stop campaign');
    } finally {
      setActionLoading(false);
    }
  };

  const handleIncludeAnyway = async (buyerId) => {
    setIncludingBuyerId(buyerId);
    try {
      await campaignsAPI.updateRecipients(id, { add: [buyerId] });
      await fetchLive();
      await loadExcluded();
      await loadBreakdown();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not include recipient');
    } finally {
      setIncludingBuyerId(null);
    }
  };

  if (loading) {
    return <div className="cd-page cd-page--state">Loading campaign...</div>;
  }

  if (error && !live) {
    return (
      <div className="cd-page">
        <button type="button" className="cd-back" onClick={() => navigate('/campaigns')}>
          <ArrowLeft className="cd-back-icon" aria-hidden="true" />
          Back to campaigns
        </button>
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  const status = live?.status || campaign?.status;
  const counts = live?.counts || { sent: 0, pending: 0, failed: 0, cancelled: 0, total: 0 };
  const pending = live?.pending || [];
  const failures = live?.failures || [];
  const total = counts.total || 1;
  const sentPct = Math.round(((counts.sent || 0) / total) * 100);
  const failedPct = Math.round(((counts.failed || 0) / total) * 100);
  const pendingPct = Math.max(0, 100 - sentPct - failedPct);

  const canPause = status === 'running';
  const canResume = status === 'paused';
  const canStop = ['running', 'scheduled', 'paused'].includes(status);
  const canManageRecipients = ['running', 'scheduled', 'paused'].includes(status);

  const templateName = campaign?.template_name || template?.name || null;
  const groupName = campaign?.group_name || (campaign?.group_id ? `Group #${campaign.group_id}` : null);
  const provider = campaign?.template_provider || null;
  const fromEmail = campaign?.template_from_email || null;
  const scheduledAt = campaign?.start_time || (status === 'scheduled' ? campaign?.scheduled_time : null);
  const templateMessages = template?.json_config?.messages;
  const messageCount = Array.isArray(templateMessages) ? templateMessages.length : null;
  const startedAt = campaign?.started_at || campaign?.launched_at || null;
  const finishedAt = campaign?.finished_at || null;

  return (
    <div className="cd-page">
      {confirmDialog}
      <button type="button" className="cd-back" onClick={() => navigate('/campaigns')}>
        <ArrowLeft className="cd-back-icon" aria-hidden="true" />
        Back to campaigns
      </button>

      <header className="cd-header">
        <div className="cd-header-main">
          <div className="cd-title-row">
            <h1 className="cd-title">{campaign?.name}</h1>
            {status && <span className={campaignStatusClass(status)}>{status}</span>}
          </div>
          {(templateName || groupName || provider) && (
            <div className="cd-header-meta">
              {[templateName, groupName, provider && `via ${provider}`].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
        {(canPause || canResume || canStop) && (
          <div className="cd-header-actions">
            {canPause && (
              <button className="btn btn-secondary" disabled={actionLoading} onClick={handlePause}>
                Pause
              </button>
            )}
            {canResume && (
              <button className="btn btn-secondary" disabled={actionLoading} onClick={handleResume}>
                Resume
              </button>
            )}
            {canStop && (
              <button className="btn btn-danger" disabled={actionLoading} onClick={handleStop}>
                Stop
              </button>
            )}
          </div>
        )}
      </header>

      {(canPause || canResume || canStop) && (
        <p className="cd-hint">
          Pause is temporary — Resume picks up exactly where it left off. Stop ends the campaign for
          good — Restart will begin again from the first message for everyone, including people who already got through.
        </p>
      )}

      {successMessage && <div className="alert alert-success cd-alert">{successMessage}</div>}
      {error && <div className="alert alert-error cd-alert"><SetupErrorMessage message={error} /></div>}

      <div className="cd-kpis">
        <div className="cd-kpi">
          <div className="cd-kpi-label">Sent</div>
          <div className="cd-kpi-value">{counts.sent || 0}</div>
        </div>
        <div className="cd-kpi">
          <div className="cd-kpi-label">Failed</div>
          <div className={`cd-kpi-value${(counts.failed || 0) > 0 ? ' cd-kpi-value--danger' : ''}`}>{counts.failed || 0}</div>
        </div>
        <div className="cd-kpi">
          <div className="cd-kpi-label">Pending</div>
          <div className="cd-kpi-value">{counts.pending || 0}</div>
        </div>
        <div className="cd-kpi">
          <div className="cd-kpi-label">Cancelled</div>
          <div className="cd-kpi-value">{counts.cancelled || 0}</div>
        </div>
        <div className="cd-kpi">
          <div className="cd-kpi-label">Total</div>
          <div className="cd-kpi-value">{counts.total || 0}</div>
        </div>
      </div>

      <div className="cd-grid">
        <section className="cd-card">
          <div className="cd-card-head">
            <h2 className="cd-card-title">Overview</h2>
          </div>
          <dl className="cd-details">
            <div className="cd-detail">
              <dt>Template</dt>
              <dd>{templateName || <span className="cd-muted">—</span>}</dd>
            </div>
            <div className="cd-detail">
              <dt>Group</dt>
              <dd>{groupName || <span className="cd-muted">—</span>}</dd>
            </div>
            <div className="cd-detail">
              <dt>Provider</dt>
              <dd>{provider || <span className="cd-muted">—</span>}</dd>
            </div>
            <div className="cd-detail">
              <dt>From</dt>
              <dd>{fromEmail || <span className="cd-muted cd-italic">Default address from settings</span>}</dd>
            </div>
            <div className="cd-detail">
              <dt>Schedule</dt>
              <dd>{scheduledAt ? formatDateTime(scheduledAt) : 'Immediately'}</dd>
            </div>
            {messageCount !== null && (
              <div className="cd-detail">
                <dt>Messages</dt>
                <dd>{messageCount} {messageCount === 1 ? 'step' : 'steps'}</dd>
              </div>
            )}
            {startedAt && (
              <div className="cd-detail">
                <dt>Launched</dt>
                <dd>{formatDateTime(startedAt)}</dd>
              </div>
            )}
            {finishedAt && (
              <div className="cd-detail">
                <dt>Finished</dt>
                <dd>{formatDateTime(finishedAt)}</dd>
              </div>
            )}
          </dl>
        </section>

        <section className="cd-card">
          <div className="cd-card-head">
            <h2 className="cd-card-title">Live status</h2>
            <span className="cd-card-sub">{sentPct}% sent</span>
          </div>
          <div className="cd-progress" role="img" aria-label={`${counts.sent || 0} sent, ${counts.failed || 0} failed, ${counts.pending || 0} pending`}>
            <div className="cd-progress-seg cd-progress-seg--sent" style={{ width: `${sentPct}%` }} />
            <div className="cd-progress-seg cd-progress-seg--failed" style={{ width: `${failedPct}%` }} />
            <div className="cd-progress-seg cd-progress-seg--pending" style={{ width: `${pendingPct}%` }} />
          </div>
          <ul className="cd-legend">
            <li><span className="cd-legend-dot cd-legend-dot--sent" />Sent <strong>{counts.sent}</strong></li>
            <li><span className="cd-legend-dot cd-legend-dot--failed" />Failed <strong>{counts.failed}</strong></li>
            <li><span className="cd-legend-dot cd-legend-dot--pending" />Pending <strong>{counts.pending}</strong></li>
            <li><span className="cd-legend-dot cd-legend-dot--cancelled" />Cancelled <strong>{counts.cancelled}</strong></li>
            <li className="cd-legend-total">Total <strong>{counts.total}</strong></li>
          </ul>
        </section>
      </div>

      {usesSendingIntelligence && (
        <div className="cd-plan">
          <SendingPlanPanel breakdown={breakdown} onRefresh={loadBreakdown} />
        </div>
      )}

      <RecipientsPanel campaignId={id} pending={pending} onChanged={refreshAfterRecipientChange} onError={setError} reasons={reasonsByBuyer} />

      {excludedBuyers.length > 0 && (
        <section className="cd-card">
          <div className="cd-card-head">
            <div>
              <h2 className="cd-card-title">
                Excluded <span className="cd-card-count">{excludedBuyers.length}</span>
              </h2>
              <p className="cd-card-sub">
                {canManageRecipients
                  ? 'Hunter flagged these emails as rejected, so they were not scheduled.'
                  : 'Hunter has flagged these emails as rejected. They will be excluded from sending once this campaign launches.'}
              </p>
            </div>
          </div>
          <ul className="cd-list">
            {excludedBuyers.map((b) => (
              <li key={b.id} className="cd-list-row">
                <span className="cd-list-main">
                  {b.business_name}
                  <span className="cd-meta-inline">({b.email})</span>
                </span>
                <button
                  type="button"
                  className="cd-link-btn"
                  disabled={includingBuyerId === b.id || !canManageRecipients}
                  onClick={() => handleIncludeAnyway(b.id)}
                  title={canManageRecipients ? undefined : 'Launch the campaign first to manage recipients'}
                >
                  Include anyway
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <MessageEditorPanel
        campaignId={id}
        campaign={campaign}
        template={template}
        pending={pending}
        onChanged={fetchLive}
        onError={setError}
        onSuccess={flashSuccess}
      />

      <FailuresPanel campaignId={id} failures={failures} onChanged={fetchLive} onError={setError} />
    </div>
  );
}

export default CampaignDetailPage;
